import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import ClubSite from './components/ClubSite';
import ErrorBoundary from './components/ErrorBoundary';
import reportWebVitals from './reportWebVitals';

/* ------------------------------------------------------------------
   One address shape belongs to the clubs: greensheetbowls.online/<club>.

   Decided here rather than inside App, which already has several early
   returns for membership forms and password resets and did not need
   another.

   The rule is simply "exactly one segment", and that is safe because
   every route this app handles has two or more — /register/:id,
   /reset-password/:token, /membership/:ref, /subscribe/:id — and
   everything the server answers itself (/api, /mail, /poster, /club,
   /club-photo, /static, /.well-known) never reaches this file at all.

   So no copy of the server's reserved list is kept here, deliberately:
   two lists drift, and this one would only ever be consulted to answer
   a question the API already answers better. An unknown single segment
   asks the server, gets a 404, and says "Club not found" — which is
   the right answer whether the club never existed or was renamed.

   The short list below is only the app's own words, to stop a future
   single-segment screen being swallowed by a club page. What a club
   may TAKE is the server's business — see server/clubAddress.js.
   ------------------------------------------------------------------ */
const APP_OWN_PATHS = new Set([
  'login', 'register', 'help', 'privacy', 'terms', 'admin', 'app', 'subscribe',
  'membership', 'reset-password', 'renew',
]);

const segments = window.location.pathname.split('/').filter(Boolean);
/* On a club's own domain (www.theirclub.co.nz) the server writes which club
   it is, and where the main app lives, into the page — see
   server/utils/clubPage.js. Only the club's page lives there; the app's
   buttons go to the main address. */
const metaContent = name => {
  const el = document.querySelector(`meta[name="${name}"]`);
  return el ? el.getAttribute('content') : null;
};
const ownDomainAppOrigin = metaContent('gs-app-origin');
const clubSlug = ownDomainAppOrigin
  ? metaContent('gs-club-slug')
  : (segments.length === 1 && !APP_OWN_PATHS.has(segments[0].toLowerCase())
    ? decodeURIComponent(segments[0])
    : null);

const root = ReactDOM.createRoot(document.getElementById('root'));
/* Wrapped so a render error shows a message with a way out, instead of
   unmounting everything to a white page. See components/ErrorBoundary.js. */
root.render(
  <React.StrictMode>
    <ErrorBoundary>
      {clubSlug ? <ClubSite slug={clubSlug} appOrigin={ownDomainAppOrigin || ''} /> : <App />}
    </ErrorBoundary>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
